import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { POSITIONS, FOOT_OPTIONS, SHOE_SIZES, MEDICAL_COVERAGES, NATIONALITIES, COMMUNITY_PASSPORT_OPTIONS, resolveOtherValue, isCustomValue } from '../constants/playerFields';
import SelectWithAdd from '../components/SelectWithAdd';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/context/AuthContext';
import Pagination from '@/components/Pagination';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, User, Trash2, Edit, LayoutGrid, List, Upload, FileWarning, X, FileDown, ClipboardList, FileSpreadsheet, Shield } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import ImageUpload from '@/components/ImageUpload';
import ScoreBadge from '@/components/ScoreBadge';

const BACKEND_URL = (window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "");
const API = `${BACKEND_URL}/api`;

// Helper function to get full photo URL
const getPhotoUrl = (photoPath) => {
  if (!photoPath) return null;
  // If it's already a full URL, return as-is
  if (photoPath.startsWith('http')) return photoPath;
  // Otherwise, prepend backend URL
  return `${BACKEND_URL}${photoPath}`;
};

// Indicador sutil del origen del registro (formulario público / Excel / manual)
const OriginIndicator = ({ player, size = 'sm', testIdSuffix = '' }) => {
  const src = player.imported_from || '';
  const iconClass = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';
  let icon = null;
  let label = '';
  let testId = '';
  if (src === 'formulario') {
    icon = <ClipboardList className={`${iconClass} text-emerald-600`} />;
    label = 'Registrado vía formulario público';
    testId = `origin-formulario-${player.id}${testIdSuffix}`;
  } else if (src && src.startsWith('excel')) {
    icon = <FileSpreadsheet className={`${iconClass} text-sky-600`} />;
    label = 'Importado desde Excel';
    testId = `origin-excel-${player.id}${testIdSuffix}`;
  } else {
    icon = <User className={`${iconClass} text-gray-400`} />;
    label = 'Carga manual';
    testId = `origin-manual-${player.id}${testIdSuffix}`;
  }
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span data-testid={testId} className="inline-flex shrink-0">
            {icon}
          </span>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const Players = () => {
  usePageTitle('Jugadores');
  const navigate = useNavigate();
  const { isScout, isAdmin, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [players, setPlayers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false); // Separate state for search loading
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [filterCategory, setFilterCategory] = useState(searchParams.get('category') || 'all');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || 'all');
  const [filterPosition, setFilterPosition] = useState(searchParams.get('position') || 'all');
  const [filterScore, setFilterScore] = useState('all');
  const [filterCertificate, setFilterCertificate] = useState(searchParams.get('filter') === 'certificados_vencidos' ? 'expired' : searchParams.get('filter') === 'certificados_por_vencer' ? 'expiring' : searchParams.get('certificate_status') || 'all');
  const [filterImported, setFilterImported] = useState(searchParams.get('imported') || 'all'); // all, excel, manual
  const [filterTeam, setFilterTeam] = useState(searchParams.get('team') || 'all');
  const [teamOptions, setTeamOptions] = useState([]);
  const [sortBy, setSortBy] = useState('created_at'); // created_at, join_date, birth_date, name
  const [sortOrder, setSortOrder] = useState('desc'); // asc, desc
  const [filterBirthYear, setFilterBirthYear] = useState(searchParams.get('birth_year') || 'all'); // Filter by birth year
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null); // { imported_count, skipped_count, total_rows, errors, warnings }
  // Valores custom cuando se elige "Otro/Otra" en el formulario de creación
  const [createCustomValues, setCreateCustomValues] = useState({ nationality: '', medical_coverage: '' });
  const handleCreateCustomChange = (field, text) => setCreateCustomValues(prev => ({ ...prev, [field]: text }));
  
  // Scout edit dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [editingPlayer, setEditingPlayer] = useState({});
  
  // Server-side pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [newPlayer, setNewPlayer] = useState({
    first_name: '',
    last_name: '',
    birth_date: '',
    document: '',
    category_id: '',
    category_ids: [],
    phone: '',
    email: '',
    address: '',
    emergency_phone: '',
    medical_coverage: '',
    height: '',
    weight: '',
    preferred_foot: '',
    position: '',
    position_secondary: '',
    origin_club: '',
    // Campos de agencia
    passport: '',
    community_passport: '',
    nationality: '',
    social_media: '',
    mother_name: '',
    mother_phone: '',
    father_name: '',
    father_phone: '',
    tutor_name: '',
    tutor_phone: '',
    shoe_size: '',
    club_trajectory: '',
    years_experience: '',
    educational_institution: '',
    academic_performance: '',
    scouted_by: '',
    status: 'active'
  });
  const [calculatedAge, setCalculatedAge] = useState(null);
  const [suggestedCategory, setSuggestedCategory] = useState(null);

  // Filter for validation status (admin only)
  const [filterValidation, setFilterValidation] = useState('all');
  // Filter by scout (admin only)
  const [filterScout, setFilterScout] = useState('all');
  const [scouts, setScouts] = useState([]);
  const [usersMap, setUsersMap] = useState({}); // Map of user ID -> user data for dynamic name lookup
  // Debounced search for server-side
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Initial load
  useEffect(() => {
    fetchPlayers(false);
    fetchCategories();
    fetchUsersAndScouts();
  }, []);

  // Listen for URL parameter changes (for notifications navigation)
  useEffect(() => {
    const certStatus = searchParams.get('certificate_status');
    if (certStatus && certStatus !== filterCertificate) {
      setFilterCertificate(certStatus);
      setCurrentPage(1);
    }
  }, [searchParams]);
  
  // Filter changes (not initial load)
  useEffect(() => {
    // Skip initial render
    if (loading) return;
    fetchPlayers(true);
  }, [currentPage, filterCategory, filterStatus, filterPosition, filterValidation, filterScout, filterCertificate, filterImported, filterTeam, filterBirthYear, filterScore, sortBy, sortOrder, debouncedSearch, itemsPerPage]);

  const handleItemsPerPageChange = (newSize) => {
    setItemsPerPage(newSize);
    setCurrentPage(1);
  };

  // Fetch all users and build map for dynamic name lookup
  const fetchUsersAndScouts = async () => {
    try {
      const response = await axios.get(`${API}/auth/users`);
      const allUsers = response.data || [];
      
      // Build map of user ID -> user data
      const userMap = {};
      allUsers.forEach(u => {
        userMap[u.id] = u;
      });
      setUsersMap(userMap);
      
      // Filter scouts for the dropdown
      const scoutUsers = allUsers.filter(u => u.role === 'scout');
      setScouts(scoutUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Cargar catálogo de equipos para el filtro
  useEffect(() => {
    axios.get(`${API}/catalogs/teams`)
      .then(res => setTeamOptions(res.data || []))
      .catch(err => console.error('Error loading teams catalog:', err));
  }, []);

  // Helper to get current user name from ID (uses updated data from usersMap)
  const getCreatorName = (player) => {
    if (!player.created_by) return null;
    
    // First check if we have the user in our map (most up-to-date)
    const user = usersMap[player.created_by];
    if (user) {
      return user.full_name || user.username;
    }
    
    // Fallback to stored name in player (may be outdated)
    return player.created_by_name;
  };

  const getCreatorRole = (player) => {
    if (!player.created_by) return null;
    const user = usersMap[player.created_by];
    return user?.role || player.created_by_role;
  };

  // Scout/captador asignado (puede diferir de quien lo ingresó al sistema)
  const getScoutName = (player) => {
    if (!player.scouted_by) return null;
    const user = usersMap[player.scouted_by];
    if (user) return user.full_name || user.username;
    return null;
  };

  const fetchPlayers = async (isSearch = false) => {
    try {
      // Only show full loading spinner on initial load, not on searches
      if (!isSearch) {
        setLoading(true);
      } else {
        setIsSearching(true);
      }
      
      // Build query params for server-side filtering
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', itemsPerPage);
      if (filterCategory !== 'all') params.append('category_id', filterCategory);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterPosition !== 'all') params.append('position', filterPosition);
      if (filterValidation !== 'all') params.append('validation_status', filterValidation);
      if (filterScout !== 'all') params.append('scouted_by', filterScout);
      if (filterCertificate !== 'all') params.append('certificate_status', filterCertificate);
      if (filterImported !== 'all') params.append('imported_from', filterImported);
      if (filterTeam !== 'all') params.append('team', filterTeam);
      if (filterBirthYear !== 'all') params.append('birth_year', filterBirthYear);
      
      // Score filter - send to server for correct pagination
      // Note: overall_score is on a 0-100 scale (percentage)
      if (filterScore !== 'all') {
        switch (filterScore) {
          case 'excellent': 
            params.append('min_score', '80');  // 80-100%
            break;
          case 'good': 
            params.append('min_score', '60');  // 60-79%
            params.append('max_score', '79.99');
            break;
          case 'average': 
            params.append('min_score', '40');  // 40-59%
            params.append('max_score', '59.99');
            break;
          case 'low': 
            params.append('max_score', '39.99');  // <40%
            break;
        }
      }
      
      if (sortBy) params.append('sort_by', sortBy);
      if (sortOrder) params.append('sort_order', sortOrder);
      if (debouncedSearch) params.append('search', debouncedSearch);
      
      const response = await axios.get(`${API}/players?${params.toString()}`);
      setPlayers(response.data.data || []);
      setTotalItems(response.data.pagination?.total || 0);
      setTotalPages(response.data.pagination?.total_pages || 1);
    } catch (error) {
      console.error('Error fetching players:', error);
      toast.error('Error al cargar jugadores');
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      // Sort categories: Sub-X by number (ascending), then Primera/Reserva at the end
      const sortedCategories = response.data.sort((a, b) => {
        const aIsSpecial = !a.name.startsWith('Sub-');
        const bIsSpecial = !b.name.startsWith('Sub-');
        
        // Special categories (Primera, Reserva) go to the end
        if (aIsSpecial && !bIsSpecial) return 1;
        if (!aIsSpecial && bIsSpecial) return -1;
        if (aIsSpecial && bIsSpecial) return a.name.localeCompare(b.name);
        
        // Both are Sub-X, sort by number
        const aNum = parseInt(a.name.replace('Sub-', '')) || 0;
        const bNum = parseInt(b.name.replace('Sub-', '')) || 0;
        return aNum - bNum;
      });
      setCategories(sortedCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Calculate age from birth date
  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    // Parse date as local time to avoid timezone issues
    const [year, month, day] = birthDate.split('-').map(Number);
    const birth = new Date(year, month - 1, day);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Extract age from category name (e.g., "Sub-15 Masculino" -> 15)
  const extractAgeFromCategory = (categoryName) => {
    const match = categoryName.match(/Sub-?(\d+)/i);
    return match ? parseInt(match[1]) : null;
  };

  // Sort categories: Sub-X ascending, then non-Sub categories (e.g., "Primera") at the end
  const sortCategories = (cats) => {
    return [...cats].sort((a, b) => {
      const ageA = extractAgeFromCategory(a.name);
      const ageB = extractAgeFromCategory(b.name);
      if (ageA != null && ageB != null) return ageA - ageB;
      if (ageA != null) return -1;
      if (ageB != null) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  };

  // Show ALL categories always; just suggest the best match.
  const getAvailableCategories = (birthDate) => {
    return sortCategories(categories);
  };

  // Get suggested category (best match for player AGE, not birth year)
  // Lógica: el jugador de 18 años juega en Sub-18, el de 17 en Sub-17, etc.
  // Si no existe Sub-<edad>, se cae a Primera (mayores) o a la Sub-X más cercana (menores).
  const getSuggestedCategory = (birthDate) => {
    if (!birthDate || !categories.length) return null;
    const age = calculateAge(birthDate);
    if (age === null || isNaN(age)) return null;

    const subCats = categories
      .map(c => {
        const m = (c.name || '').match(/sub-?\s*(\d+)/i);
        return m ? { cat: c, num: parseInt(m[1], 10) } : null;
      })
      .filter(x => x && !isNaN(x.num));

    const otherCats = categories.filter(c => !(c.name || '').match(/sub-?\s*\d+/i));
    const primera = otherCats.find(c => (c.name || '').toLowerCase().includes('primera')) || otherCats[0];

    if (!subCats.length) return primera || null;

    // 1. Exact age match: Sub-18 para jugador de 18 años
    const exact = subCats.find(x => x.num === age);
    if (exact) return exact.cat;

    const maxSub = Math.max(...subCats.map(x => x.num));
    const minSub = Math.min(...subCats.map(x => x.num));

    // Mayor a la Sub más grande → Primera
    if (age > maxSub) return primera || null;

    // Menor a la Sub más chica → Sub más chica
    if (age < minSub) {
      return subCats.reduce((acc, x) => (acc.num < x.num ? acc : x)).cat;
    }

    // Caso raro: edad entre min y max pero sin exact (no suele pasar) → cercano hacia arriba
    const closest = subCats
      .filter(x => x.num >= age)
      .reduce((acc, x) => (!acc || x.num < acc.num ? x : acc), null);
    return closest ? closest.cat : (primera || null);
  };

  // Handle birth date change
  const handleBirthDateChange = (birthDate) => {
    const age = calculateAge(birthDate);
    setCalculatedAge(age);

    const suggested = getSuggestedCategory(birthDate);
    setSuggestedCategory(suggested);

    if (suggested) {
      // Always replace with the suggested category when the date changes
      setNewPlayer(prev => ({
        ...prev,
        birth_date: birthDate,
        category_id: suggested.id,
        category_ids: [suggested.id]
      }));
    } else {
      setNewPlayer(prev => ({ ...prev, birth_date: birthDate }));
    }
  };

  const handleCreatePlayer = async (e) => {
    e.preventDefault();

    // Validación explícita antes de enviar (los campos con `required` ya lo hacen vía HTML,
    // pero reforzamos para evitar espacios en blanco y dar mensajes claros)
    const missing = [];
    if (!newPlayer.first_name?.trim()) missing.push('Nombre');
    if (!newPlayer.last_name?.trim()) missing.push('Apellido');
    if (!newPlayer.birth_date) missing.push('Fecha de Nacimiento');
    if (!newPlayer.document?.trim()) missing.push('Documento');
    if (!newPlayer.phone?.trim()) missing.push('Celular');
    if (!newPlayer.position?.trim()) missing.push('Posición');
    if (!newPlayer.preferred_foot?.trim()) missing.push('Pierna Hábil');
    if (isAdmin && !newPlayer.scouted_by?.trim()) missing.push('Scout / Captador');
    if (!newPlayer.category_ids || newPlayer.category_ids.length === 0) missing.push('Categoría');

    // Al menos un contacto de padre/madre/tutor (nombre + teléfono)
    const hasMother = newPlayer.mother_name?.trim() && newPlayer.mother_phone?.trim();
    const hasFather = newPlayer.father_name?.trim() && newPlayer.father_phone?.trim();
    const hasTutor  = newPlayer.tutor_name?.trim()  && newPlayer.tutor_phone?.trim();
    if (!hasMother && !hasFather && !hasTutor) {
      missing.push('Contacto familiar (Madre, Padre o Tutor: nombre + teléfono)');
    }

    if (missing.length > 0) {
      toast.error(`Faltan campos obligatorios: ${missing.join(', ')}`);
      return;
    }

    try {
      // Clean up empty strings to null for optional fields
      const cleanedData = {};
      for (const [key, value] of Object.entries(newPlayer)) {
        if (value === '' || value === null || value === undefined) {
          // Don't send empty optional fields (except required ones)
          if (!['first_name', 'last_name', 'birth_date', 'category_ids'].includes(key)) {
            continue;
          }
        }
        // Skip empty arrays
        if (Array.isArray(value) && value.length === 0 && key !== 'category_ids') {
          continue;
        }
        cleanedData[key] = value;
      }
      
      // Ensure category_id is set from category_ids for backwards compatibility
      if (cleanedData.category_ids && cleanedData.category_ids.length > 0) {
        cleanedData.category_id = cleanedData.category_ids[0];
      }
      
      // Convert numeric fields
      if (cleanedData.height) cleanedData.height = parseFloat(cleanedData.height);
      if (cleanedData.weight) cleanedData.weight = parseFloat(cleanedData.weight);
      if (cleanedData.years_experience) cleanedData.years_experience = parseInt(cleanedData.years_experience);
      
      // Resolver valores "Otro/Otra"
      if (cleanedData.nationality) cleanedData.nationality = resolveOtherValue(cleanedData.nationality, createCustomValues.nationality);
      if (cleanedData.medical_coverage) cleanedData.medical_coverage = resolveOtherValue(cleanedData.medical_coverage, createCustomValues.medical_coverage);

      await axios.post(`${API}/players`, cleanedData);
      toast.success('Jugador creado exitosamente');
      setShowCreateDialog(false);
      resetForm();
      fetchPlayers();
    } catch (error) {
      console.error('Error creating player:', error);
      // Handle validation errors from Pydantic
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const errorMsg = detail.map(e => e.msg).join(', ');
        toast.error(errorMsg);
      } else {
        toast.error(detail || 'Error al crear jugador');
      }
    }
  };

  const handleDeletePlayer = async (playerId, playerName) => {
    if (window.confirm(`¿Estás seguro de eliminar a ${playerName}?`)) {
      try {
        await axios.delete(`${API}/players/${playerId}`);
        toast.success('Jugador eliminado exitosamente');
        fetchPlayers();
      } catch (error) {
        console.error('Error deleting player:', error);
        toast.error('Error al eliminar jugador');
      }
    }
  };

  // Validate player (admin only)
  const handleValidatePlayer = async (playerId) => {
    try {
      await axios.post(`${API}/players/${playerId}/validate`);
      toast.success('Jugador validado exitosamente');
      fetchPlayers();
    } catch (error) {
      console.error('Error validating player:', error);
      toast.error(error.response?.data?.detail || 'Error al validar jugador');
    }
  };

  // Open edit dialog for scout
  const openEditDialog = (player) => {
    // Double check - if player is validated, show friendly message
    if (player.validation_status === 'validated') {
      toast.info(
        'Este jugador ya fue validado por un administrador. Si necesitas hacer cambios, comunícate con el Director Deportivo.',
        { duration: 5000 }
      );
      return;
    }
    
    setSelectedPlayer(player);
    setEditingPlayer({
      first_name: player.first_name,
      last_name: player.last_name,
      birth_date: player.birth_date,
      document: player.document,
      phone: player.phone || '',
      email: player.email || '',
      address: player.address || '',
      category_id: player.category_id,
      position: player.position || '',
      origin_club: player.origin_club || ''
    });
    setShowEditDialog(true);
  };

  // Handle scout edit submit
  const handleEditPlayer = async () => {
    if (!selectedPlayer) return;
    
    try {
      // Clean up empty strings to null for optional fields
      const cleanedData = {};
      for (const [key, value] of Object.entries(editingPlayer)) {
        if (value === '' || value === null || value === undefined) {
          // Don't send empty optional fields
          continue;
        }
        cleanedData[key] = value;
      }
      
      await axios.put(`${API}/players/${selectedPlayer.id}`, cleanedData);
      toast.success('Jugador actualizado exitosamente');
      setShowEditDialog(false);
      setSelectedPlayer(null);
      fetchPlayers();
    } catch (error) {
      console.error('Error updating player:', error);
      
      // Handle 403 - Player already validated
      if (error.response?.status === 403) {
        toast.info(
          'Este jugador ya fue validado por un administrador. Si necesitas hacer cambios, comunícate con el Director Deportivo.',
          { duration: 5000 }
        );
        setShowEditDialog(false);
        setSelectedPlayer(null);
        fetchPlayers(); // Refresh to update UI
        return;
      }
      
      // Handle validation errors from Pydantic
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const errorMsg = detail.map(e => e.msg).join(', ');
        toast.error(errorMsg);
      } else {
        toast.error(detail || 'Error al actualizar jugador');
      }
    }
  };

  const handleImportExcel = async () => {
    if (!importFile) {
      toast.error('Por favor selecciona un archivo');
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await axios.post(`${API}/players/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const { imported_count, skipped_count = 0, total_rows, errors = [], warnings = [] } = response.data;

      // Guardar resultado completo para mostrar en el modal de detalles
      setImportResult({ imported_count, skipped_count, total_rows, errors, warnings });

      // Toast resumen claro: importados / duplicados / errores
      const parts = [`Importados: ${imported_count}`];
      if (skipped_count > 0) parts.push(`Duplicados: ${skipped_count}`);
      if (errors.length > 0) parts.push(`Errores: ${errors.length}`);
      const summary = `${parts.join(' · ')} (de ${total_rows} filas)`;

      if (errors.length > 0) {
        toast.warning(summary);
      } else if (imported_count === 0 && skipped_count > 0) {
        // Caso típico: re-import de la misma planilla. No es un error, todos ya existían.
        toast.info(`${summary}. Todos los jugadores ya estaban cargados.`);
      } else {
        toast.success(summary);
      }

      setShowImportDialog(false);
      setImportFile(null);
      fetchPlayers();
    } catch (error) {
      console.error('Error importing players:', error);
      toast.error(error.response?.data?.detail || 'Error al importar jugadores');
    } finally {
      setImporting(false);
    }
  };

  const resetForm = () => {
    setNewPlayer({
      first_name: '', last_name: '', birth_date: '', document: '',
      category_id: '', category_ids: [],
      phone: '', email: '', address: '', emergency_phone: '',
      medical_coverage: '', height: '', weight: '',
      preferred_foot: '', position: '', position_secondary: '', origin_club: '',
      passport: '', community_passport: '', nationality: '', social_media: '',
      mother_name: '', mother_phone: '', father_name: '', father_phone: '',
      tutor_name: '', tutor_phone: '', shoe_size: '', club_trajectory: '',
      years_experience: '', educational_institution: '', academic_performance: '',
      scouted_by: '',
      status: 'active'
    });
    setCreateCustomValues({ nationality: '', medical_coverage: '' });
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Sin categoría';
  };

  // Update URL params when filters change (use debounced search to avoid focus loss)
  useEffect(() => {
    const params = {};
    if (debouncedSearch) params.search = debouncedSearch;
    if (filterCategory !== 'all') params.category = filterCategory;
    if (filterStatus !== 'all') params.status = filterStatus;
    if (filterValidation !== 'all') params.validation = filterValidation;
    setSearchParams(params, { replace: true });
  }, [debouncedSearch, filterCategory, filterStatus, filterValidation, setSearchParams]);

  // No client-side filtering needed - server handles all filters including score
  const filteredPlayers = players;

  // Reset to page 1 when server-side filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterCategory, filterStatus, filterPosition, filterValidation, filterCertificate, filterImported, filterTeam, filterBirthYear, filterScore, sortBy, sortOrder, debouncedSearch]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'trial': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Activo';
      case 'inactive': return 'Inactivo';
      case 'trial': return 'Prueba';
      default: return status;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
            <p className="mt-4 text-gray-600">Cargando jugadores...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Jugadores</h1>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-gray-600">{totalItems} jugador(es) encontrado(s)</p>
              {filterCategory !== 'all' && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-brand-100 text-brand-800">
                  Categoría: {getCategoryName(filterCategory)}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* View mode toggle - shown for all roles (admin, user, scout) */}
            <div className="hidden sm:flex bg-gray-100 rounded-lg p-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' ? 'bg-white shadow-sm' : ''}
                data-testid="view-mode-list"
              >
                <List className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewMode('grid')}
                className={viewMode === 'grid' ? 'bg-white shadow-sm' : ''}
                data-testid="view-mode-grid"
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
            </div>
            {/* Import + Download buttons - only for admin/user, not scout */}
            {!isScout && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShowImportDialog(true)}
                  className="hidden sm:flex border-brand-600 text-brand-600 hover:bg-brand-50"
                  data-testid="import-players-button"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Importar Excel
                </Button>
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      const res = await axios.get(`${API}/players/export-template`, {
                        responseType: 'blob',
                      });
                      const blob = res.data;
                      if (!blob || blob.size < 100) {
                        throw new Error('Archivo recibido vacío');
                      }
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'plantilla_jugadores.xlsx';
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      setTimeout(() => URL.revokeObjectURL(url), 100);
                      toast.success('Plantilla descargada');
                    } catch (err) {
                      console.error('[download-template]', err);
                      const detail = err.response?.data?.detail || err.message || 'desconocido';
                      toast.error(`Error al descargar plantilla: ${detail}`);
                    }
                  }}
                  className="hidden sm:flex"
                  data-testid="download-template-button"
                  title="Descargar plantilla Excel"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  Plantilla
                </Button>
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      const res = await axios.get(`${API}/players/export-data`, {
                        responseType: 'blob',
                      });
                      const blob = res.data;
                      if (!blob || blob.size < 100) {
                        throw new Error('Archivo recibido vacío');
                      }
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'jugadores.xlsx';
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      setTimeout(() => URL.revokeObjectURL(url), 100);
                      toast.success('Jugadores exportados');
                    } catch (err) {
                      console.error('[export-data]', err);
                      const detail = err.response?.data?.detail || err.message || 'desconocido';
                      toast.error(`Error al exportar jugadores: ${detail}`);
                    }
                  }}
                  className="hidden sm:flex"
                  data-testid="export-data-button"
                  title="Exportar jugadores a Excel"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Exportar
                </Button>
              </>
            )}
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="bg-brand-600 hover:bg-brand-700"
              data-testid="create-player-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Nuevo Jugador</span>
              <span className="sm:hidden">Nuevo</span>
            </Button>
          </div>
        </div>

        {/* Active Filter Banner */}
        {filterCertificate !== 'all' && (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="py-3 px-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileWarning className="h-5 w-5 text-amber-600" />
                  <span className="text-sm font-medium text-amber-800">
                    {filterCertificate === 'expired' 
                      ? 'Mostrando jugadores con certificados VENCIDOS' 
                      : 'Mostrando jugadores con certificados POR VENCER (30 días)'}
                  </span>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="bg-white hover:bg-amber-100 border-amber-300 text-amber-700"
                  onClick={() => {
                    setFilterCertificate('all');
                    setSearchParams({}, { replace: true });
                  }}
                >
                  <X className="h-4 w-4 mr-1" />
                  Quitar filtro
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters - simplified for scout */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-3">
              <div className="relative flex-1 min-w-[200px]">
                {isSearching ? (
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-brand-600 border-t-transparent"></div>
                  </div>
                ) : (
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                )}
                <Input
                  placeholder="Buscar por nombre o documento..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 h-9 text-sm"
                  data-testid="search-player-input"
                />
              </div>
              {/* Hide advanced filters for scout */}
              {!isScout && (
                <>
                  <Select value={filterCategory} onValueChange={setFilterCategory}>
                    <SelectTrigger className="w-[180px] h-9 text-sm">
                      <SelectValue placeholder="Categoría" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas las categorías</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filterPosition} onValueChange={setFilterPosition}>
                    <SelectTrigger className="w-[160px] h-9 text-sm">
                      <SelectValue placeholder="Posición" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas las posiciones</SelectItem>
                      <SelectItem value="Golero">Golero</SelectItem>
                      <SelectItem value="Defensa">Defensa</SelectItem>
                      <SelectItem value="Mediocampista">Mediocampista</SelectItem>
                      <SelectItem value="Delantero">Delantero</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterTeam} onValueChange={setFilterTeam}>
                    <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="filter-team">
                      <SelectValue placeholder="Equipo" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="all">Todos los equipos</SelectItem>
                      <SelectItem value="__none__">Sin equipo asignado</SelectItem>
                      {teamOptions.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filterScore} onValueChange={setFilterScore}>
                    <SelectTrigger className="w-[140px] h-9 text-sm">
                      <SelectValue placeholder="Índice" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los índices</SelectItem>
                      <SelectItem value="excellent">Excelente (80-100)</SelectItem>
                      <SelectItem value="good">Bueno (60-79)</SelectItem>
                      <SelectItem value="average">Regular (40-59)</SelectItem>
                      <SelectItem value="low">Bajo (&lt;40)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-[130px] h-9 text-sm">
                      <SelectValue placeholder="Estado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los estados</SelectItem>
                      <SelectItem value="active">Activo</SelectItem>
                      <SelectItem value="inactive">Inactivo</SelectItem>
                      <SelectItem value="injured">Lesionado</SelectItem>
                      <SelectItem value="trial">Prueba</SelectItem>
                    </SelectContent>
                  </Select>
                  {/* Validation filter - only for admin */}
                  {isAdmin && (
                    <Select value={filterValidation} onValueChange={setFilterValidation}>
                      <SelectTrigger className={`w-[180px] h-9 text-sm ${filterValidation === 'pending' ? 'border-amber-500 bg-amber-50' : ''}`}>
                        <SelectValue placeholder="Validación" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="pending">⚠️ Pendientes de validar</SelectItem>
                        <SelectItem value="validated">✓ Validados</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  {/* Scout filter - only for admin */}
                  {isAdmin && scouts.length > 0 && (
                    <Select value={filterScout} onValueChange={setFilterScout}>
                      <SelectTrigger className="w-[160px] h-9 text-sm" data-testid="filter-scout-select">
                        <SelectValue placeholder="Captador" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos los captadores</SelectItem>
                        {scouts.map(scout => (
                          <SelectItem key={scout.id} value={scout.id}>
                            {scout.full_name || scout.username || `${scout.first_name || ''} ${scout.last_name || ''}`.trim()}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {/* Import source filter */}
                  {isAdmin && (
                    <Select value={filterImported} onValueChange={setFilterImported}>
                      <SelectTrigger className="w-[150px] h-9 text-sm" data-testid="filter-imported-select">
                        <SelectValue placeholder="Origen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos los orígenes</SelectItem>
                        <SelectItem value="excel">📥 Importados Excel</SelectItem>
                        <SelectItem value="manual">✏️ Creados manual</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  {/* Birth year filter */}
                  <Select value={filterBirthYear} onValueChange={setFilterBirthYear}>
                    <SelectTrigger className="w-[130px] h-9 text-sm" data-testid="filter-birth-year-select">
                      <SelectValue placeholder="Año Nac." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los años</SelectItem>
                      {Array.from({ length: 20 }, (_, i) => new Date().getFullYear() - 5 - i).map(year => (
                        <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* Sort selector */}
                  <Select value={`${sortBy}-${sortOrder}`} onValueChange={(val) => {
                    const [field, order] = val.split('-');
                    setSortBy(field);
                    setSortOrder(order);
                  }}>
                    <SelectTrigger className="w-[170px] h-9 text-sm" data-testid="sort-select">
                      <SelectValue placeholder="Ordenar por" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="created_at-desc">📅 Más recientes</SelectItem>
                      <SelectItem value="created_at-asc">📅 Más antiguos</SelectItem>
                      <SelectItem value="join_date-desc">🏟️ Ingreso reciente</SelectItem>
                      <SelectItem value="join_date-asc">🏟️ Ingreso antiguo</SelectItem>
                      <SelectItem value="birth_date-desc">🎂 Más jóvenes</SelectItem>
                      <SelectItem value="birth_date-asc">🎂 Más veteranos</SelectItem>
                      <SelectItem value="name-asc">🔤 Nombre A-Z</SelectItem>
                      <SelectItem value="name-desc">🔤 Nombre Z-A</SelectItem>
                    </SelectContent>
                  </Select>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {filteredPlayers.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <User className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">No hay jugadores para mostrar</p>
              {isScout ? (
                <div className="text-sm text-gray-500 mt-3 space-y-2 max-w-md mx-auto">
                  <p>Como scout, podés ver:</p>
                  <ul className="text-left list-disc list-inside text-xs space-y-1">
                    <li>Jugadores de las <strong>categorías que el admin te asignó</strong></li>
                    <li>Jugadores que vos mismo cargaste o te asignaron como captador</li>
                  </ul>
                  <p className="text-xs mt-3 text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                    💡 Si no ves jugadores, pedile al admin que te asigne <strong>categorías</strong> en la sección Usuarios — o cargá tu primer jugador con el botón <strong>+ Nuevo</strong>.
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-500 mt-1">Probá ajustar los filtros o creá uno nuevo</p>
              )}
            </CardContent>
          </Card>
        ) : viewMode === 'list' ? (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Jugador</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Club</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Edad</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Categoría</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Índice</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Posición</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Captador</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Estado</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredPlayers.map(player => {
                      const isPending = player.validation_status === 'pending';
                      return (
                      <tr 
                        key={player.id} 
                        className={`hover:bg-gray-50 cursor-pointer ${isPending ? 'bg-amber-50' : ''}`} 
                        onClick={() => navigate(`/players/${player.id}`)} 
                        data-testid={`player-row-${player.id}`}
                      >
                        <td className="px-4 py-2 whitespace-nowrap">
                          <div className="flex items-center">
                            <Avatar className="h-7 w-7">
                              <AvatarImage src={getPhotoUrl(player.photo_url)} />
                              <AvatarFallback className="bg-brand-100 text-brand-700 text-xs">
                                {player.first_name[0]}{player.last_name[0]}
                              </AvatarFallback>
                            </Avatar>
                            <div className="ml-2">
                              <p className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                                {player.first_name} {player.last_name}
                                <OriginIndicator player={player} size="sm" />
                              </p>
                              {player.document && <p className="text-xs text-gray-500 mt-0.5">{player.document}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <div className="flex items-center gap-1.5 text-sm text-gray-900" title={player.contract_club_name || player.origin_club || 'Sin club'}>
                            <Shield className="w-4 h-4 text-gray-400 shrink-0" />
                            <span className="truncate max-w-[120px] sm:max-w-[180px]">
                              {player.contract_club_name || player.origin_club || <span className="italic text-gray-500">Sin club</span>}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{calculateAge(player.birth_date)} años</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                          {(player.category_ids || [player.category_id]).filter(Boolean).map(catId => getCategoryName(catId)).join(' / ') || '-'}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <ScoreBadge score={player.overall_score} size="sm" showLabel={false} max={100} />
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{player.position || '-'}</td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          {getScoutName(player) ? (
                            <div className="flex items-center gap-1">
                              <span className="text-sm text-gray-700">{getScoutName(player)}</span>
                              <Badge className="bg-purple-100 text-purple-700 text-xs">Scout</Badge>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <div className="flex gap-1 flex-wrap">
                            {isPending && (
                              <Badge className="bg-amber-500 text-white text-xs">Pendiente</Badge>
                            )}
                            <Badge className={getStatusColor(player.status)}>
                              {getStatusLabel(player.status)}
                            </Badge>
                          </div>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-brand-600 hover:bg-brand-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/players/${player.id}`);
                              }}
                              data-testid={`view-player-${player.id}`}
                              title="Ver ficha"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {isPending && !isScout && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-green-600 hover:bg-green-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleValidatePlayer(player.id);
                                }}
                                data-testid={`validate-player-${player.id}`}
                                title="Validar jugador"
                              >
                                ✓
                              </Button>
                            )}
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:bg-red-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeletePlayer(player.id, `${player.first_name} ${player.last_name}`);
                                }}
                                data-testid={`delete-player-${player.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={handleItemsPerPageChange}
              itemName="jugador(es)"
            />
          </Card>
        ) : (
          <>
            {/* Cards / grid view */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredPlayers.map(player => {
                const isPending = player.validation_status === 'pending';
                // Scout puede editar sus propios jugadores (creados por él o asignados como captador)
                const isMine = !!user?.id && (player.created_by === user.id || player.scouted_by === user.id);
                const canEdit = isScout ? isMine : true;
                
                return (
                  <Card
                    key={player.id}
                    className={`card-hover cursor-pointer ${isPending ? 'border-2 border-amber-400 bg-amber-50' : ''}`}
                    onClick={() => navigate(`/players/${player.id}`)}
                    data-testid={`player-card-${player.id}`}
                  >
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <Avatar className="h-12 w-12">
                            <AvatarImage src={getPhotoUrl(player.photo_url)} />
                            <AvatarFallback className="bg-brand-100 text-brand-700">
                              {player.first_name[0]}{player.last_name[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <h3 className="font-semibold text-gray-900 flex items-center gap-1.5">
                              {player.first_name} {player.last_name}
                              <OriginIndicator player={player} size="md" testIdSuffix="-grid" />
                            </h3>
                            <p className="text-sm text-gray-500">{calculateAge(player.birth_date)} años</p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {isPending && (
                            <Badge className="bg-amber-500 text-white text-xs">
                              Pendiente
                            </Badge>
                          )}
                          <Badge className={getStatusColor(player.status)}>
                            {getStatusLabel(player.status)}
                          </Badge>
                        </div>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between items-center gap-2">
                          <span className="text-gray-600 shrink-0">Club:</span>
                          <span className="font-medium text-gray-900 truncate" title={player.contract_club_name || player.origin_club || 'Sin club'}>
                            {player.contract_club_name || player.origin_club || <span className="italic text-gray-500">Sin club</span>}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Categoría:</span>
                          <span className="font-medium text-gray-900">
                            {(player.category_ids || [player.category_id]).filter(Boolean).map(catId => getCategoryName(catId)).join(' / ') || '-'}
                          </span>
                        </div>
                        {player.position && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Posición:</span>
                            <span className="font-medium text-gray-900">{player.position}</span>
                          </div>
                        )}
                        {getScoutName(player) && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Captador:</span>
                            <span className="font-medium text-gray-900">{getScoutName(player)}</span>
                          </div>
                        )}
                      </div>
                      <div className="mt-4 pt-4 border-t border-gray-200 flex gap-2">
                        {isScout ? (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 text-brand-600 border-brand-600 hover:bg-brand-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/players/${player.id}`);
                              }}
                              data-testid={`view-player-${player.id}`}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Ver Detalles
                            </Button>
                            {canEdit && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-amber-600 border-amber-600 hover:bg-amber-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditDialog(player);
                                }}
                                data-testid={`edit-player-${player.id}`}
                                title="Editar"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            )}
                          </>
                        ) : (
                          // Admin/User view
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 text-brand-600 border-brand-600 hover:bg-brand-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/players/${player.id}`);
                              }}
                              data-testid={`view-player-${player.id}`}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Ver Detalles
                            </Button>
                            {isPending && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-green-600 border-green-600 hover:bg-green-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleValidatePlayer(player.id);
                                }}
                                data-testid={`validate-player-${player.id}`}
                              >
                                ✓ Validar
                              </Button>
                            )}
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:bg-red-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeletePlayer(player.id, `${player.first_name} ${player.last_name}`);
                                }}
                                data-testid={`delete-player-${player.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            {totalItems > itemsPerPage && (
              <Card>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  itemName="jugador(es)"
                />
              </Card>
            )}
          </>
        )}
      </div>

      {/* Import Excel Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Importar Jugadores desde Excel</DialogTitle>
            <DialogDescription>
              Sube un archivo Excel con los datos de los jugadores
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Archivo Excel (.xlsx, .xls)</Label>
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setImportFile(e.target.files[0])}
                data-testid="import-file-input"
              />
            </div>
            <div className="bg-brand-50 border border-brand-200 rounded-lg p-4 text-sm">
              <p className="font-semibold text-brand-900 mb-2">Columnas esperadas:</p>
              <ul className="text-brand-700 space-y-1 text-xs">
                <li>• <strong>Nombre</strong> (requerido)</li>
                <li>• <strong>Apellido</strong> (requerido)</li>
                <li>• <strong>Documento</strong> (requerido)</li>
                <li>• <strong>Fecha de Nacimiento</strong> (requerido, formato: DD/MM/YYYY)</li>
                <li>• Categoría, Teléfono, Email, Dirección, Altura, Peso, Pierna, Posición, Club (opcionales)</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowImportDialog(false);
                setImportFile(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleImportExcel}
              disabled={!importFile || importing}
              className="bg-brand-600 hover:bg-brand-700"
              data-testid="submit-import"
            >
              {importing ? 'Importando...' : 'Importar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Result Detail Dialog — shows full breakdown after import */}
      <Dialog open={importResult !== null} onOpenChange={(open) => !open && setImportResult(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="import-result-dialog">
          <DialogHeader>
            <DialogTitle>Resultado de la importación</DialogTitle>
            <DialogDescription>
              Detalle completo de qué se importó, qué se saltó y qué falló.
            </DialogDescription>
          </DialogHeader>
          {importResult && (
            <div className="space-y-4">
              {/* Stats summary cards */}
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                  <div className="text-xs text-emerald-700 font-medium">Importados</div>
                  <div className="text-2xl font-bold text-emerald-900">{importResult.imported_count}</div>
                </div>
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <div className="text-xs text-amber-700 font-medium">Duplicados (saltados)</div>
                  <div className="text-2xl font-bold text-amber-900">{importResult.skipped_count}</div>
                </div>
                <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                  <div className="text-xs text-red-700 font-medium">Con errores</div>
                  <div className="text-2xl font-bold text-red-900">{importResult.errors.length}</div>
                </div>
              </div>

              <div className="text-sm text-gray-600">
                Total de filas en la planilla: <strong>{importResult.total_rows}</strong>
              </div>

              {importResult.skipped_count > 0 && (
                <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                  <strong>Duplicados:</strong> son jugadores que ya existían en el sistema (mismo nombre + apellido + fecha de nacimiento). No se modificaron.
                </div>
              )}

              {/* Errors list */}
              {importResult.errors.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-red-900 text-sm">
                    ❌ Errores ({importResult.errors.length}) — estas filas NO se importaron
                  </h4>
                  <ul className="text-xs space-y-1 bg-red-50 border border-red-200 rounded-md p-3 max-h-48 overflow-y-auto">
                    {importResult.errors.map((err, i) => (
                      <li key={i} className="text-red-800">• {err}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Warnings list */}
              {importResult.warnings.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-amber-900 text-sm">
                    ⚠️ Avisos ({importResult.warnings.length}) — los jugadores se importaron pero con datos parciales
                  </h4>
                  <ul className="text-xs space-y-1 bg-amber-50 border border-amber-200 rounded-md p-3 max-h-48 overflow-y-auto">
                    {importResult.warnings.map((w, i) => (
                      <li key={i} className="text-amber-800">• {w}</li>
                    ))}
                  </ul>
                </div>
              )}

              {importResult.errors.length === 0 && importResult.warnings.length === 0 && importResult.imported_count > 0 && (
                <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800">
                  ✅ Importación limpia: todos los jugadores se cargaron sin errores ni avisos.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setImportResult(null)} data-testid="close-import-result">
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Crear Nuevo Jugador</DialogTitle>
            <DialogDescription>
              Completa la información básica del jugador. Podrás agregar más detalles después.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreatePlayer}>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">Nombre *</Label>
                  <Input
                    id="first_name"
                    value={newPlayer.first_name}
                    onChange={(e) => setNewPlayer({...newPlayer, first_name: e.target.value})}
                    required
                    data-testid="player-first-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Apellido *</Label>
                  <Input
                    id="last_name"
                    value={newPlayer.last_name}
                    onChange={(e) => setNewPlayer({...newPlayer, last_name: e.target.value})}
                    required
                    data-testid="player-last-name-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="birth_date">Fecha de Nacimiento *</Label>
                  <Input
                    id="birth_date"
                    type="date"
                    value={newPlayer.birth_date}
                    onChange={(e) => handleBirthDateChange(e.target.value)}
                    required
                    data-testid="player-birth-date-input"
                  />
                  {calculatedAge !== null && (
                    <p className="text-sm text-brand-600 font-medium">
                      ✓ Edad calculada: {calculatedAge} años
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="document">Documento *</Label>
                  <Input
                    id="document"
                    value={newPlayer.document}
                    onChange={(e) => setNewPlayer({...newPlayer, document: e.target.value})}
                    required
                    data-testid="player-document-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Categorías * (máx. 2)</Label>
                <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-gray-50">
                  {(newPlayer.birth_date ? getAvailableCategories(newPlayer.birth_date) : sortCategories(categories)).map(cat => {
                    const isSelected = newPlayer.category_ids.includes(cat.id);
                    const canSelect = isSelected || newPlayer.category_ids.length < 2;
                    
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        disabled={!canSelect && !isSelected}
                        onClick={() => {
                          let newIds;
                          if (isSelected) {
                            newIds = newPlayer.category_ids.filter(id => id !== cat.id);
                          } else if (newPlayer.category_ids.length < 2) {
                            newIds = [...newPlayer.category_ids, cat.id];
                          } else {
                            return;
                          }
                          setNewPlayer({
                            ...newPlayer,
                            category_ids: newIds,
                            category_id: newIds[0] || ''
                          });
                        }}
                        className={`px-3 py-1.5 text-sm rounded-full border transition-all ${
                          isSelected 
                            ? 'bg-brand-600 text-white border-brand-600' 
                            : canSelect
                              ? 'bg-white text-gray-700 border-gray-300 hover:border-brand-400'
                              : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                        }`}
                        data-testid={`category-${cat.id}`}
                      >
                        {isSelected && <span className="mr-1">✓</span>}
                        {cat.name}
                        {suggestedCategory?.id === cat.id && !isSelected && " ⭐"}
                      </button>
                    );
                  })}
                </div>
                {newPlayer.category_ids.length === 0 && (
                  <p className="text-xs text-red-600">Selecciona al menos una categoría</p>
                )}
                {suggestedCategory && calculatedAge && (
                  <p className="text-xs text-gray-600">
                    💡 Basado en {calculatedAge} años, se recomienda: <strong>{suggestedCategory.name}</strong> ⭐
                  </p>
                )}
                {suggestedCategory && newPlayer.category_ids.length > 0 && !newPlayer.category_ids.includes(suggestedCategory.id) && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="category-mismatch-warning">
                    ⚠️ Seleccionaste una categoría distinta a la sugerida ({suggestedCategory.name}). Podés guardar igual — sólo es una advertencia.
                  </p>
                )}
                {calculatedAge && newPlayer.birth_date && !suggestedCategory && (
                  <p className="text-xs text-amber-600">
                    ⚠️ No encontramos una categoría exacta para {calculatedAge} años. Seleccioná la que corresponda.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Celular *</Label>
                  <Input
                    id="phone"
                    value={newPlayer.phone}
                    onChange={(e) => setNewPlayer({...newPlayer, phone: e.target.value})}
                    placeholder="Ej: 099 123 456"
                    required
                    data-testid="player-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newPlayer.email}
                    onChange={(e) => setNewPlayer({...newPlayer, email: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="emergency_phone">Teléfono Familiar/Emergencia</Label>
                  <Input
                    id="emergency_phone"
                    value={newPlayer.emergency_phone || ''}
                    onChange={(e) => setNewPlayer({...newPlayer, emergency_phone: e.target.value})}
                    placeholder="Ej: 099123456"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="medical_coverage">Mutualista / Cobertura Médica</Label>
                  <SelectWithAdd
                    catalogType="medical_coverages"
                    value={newPlayer.medical_coverage}
                    onChange={(value) => setNewPlayer({...newPlayer, medical_coverage: value})}
                    placeholder="Selecciona mutualista..."
                    testId="new-player-medical-coverage"
                    addLabel="Agregar mutualista"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Dirección</Label>
                <Input
                  id="address"
                  value={newPlayer.address}
                  onChange={(e) => setNewPlayer({...newPlayer, address: e.target.value})}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="height">Altura (cm)</Label>
                  <Input
                    id="height"
                    type="number"
                    step="0.1"
                    value={newPlayer.height}
                    onChange={(e) => setNewPlayer({...newPlayer, height: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="weight">Peso (kg)</Label>
                  <Input
                    id="weight"
                    type="number"
                    step="0.1"
                    value={newPlayer.weight}
                    onChange={(e) => setNewPlayer({...newPlayer, weight: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="preferred_foot">Pierna Hábil *</Label>
                  <Select
                    value={newPlayer.preferred_foot}
                    onValueChange={(value) => setNewPlayer({...newPlayer, preferred_foot: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona" />
                    </SelectTrigger>
                    <SelectContent>
                      {FOOT_OPTIONS.map(f => (
                        <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="position">Posición Principal *</Label>
                  <SelectWithAdd
                    catalogType="positions"
                    value={newPlayer.position}
                    onChange={(value) => setNewPlayer({...newPlayer, position: value})}
                    placeholder="Selecciona posición..."
                    testId="new-player-position"
                    addLabel="Agregar posición"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="position_secondary">Posición Secundaria / Aclaración</Label>
                  <Input
                    id="position_secondary"
                    value={newPlayer.position_secondary}
                    onChange={(e) => setNewPlayer({...newPlayer, position_secondary: e.target.value})}
                    placeholder="Ej: también juega de extremo"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nationality">Nacionalidad</Label>
                  <SelectWithAdd
                    catalogType="nationalities"
                    value={newPlayer.nationality}
                    onChange={(value) => setNewPlayer({...newPlayer, nationality: value})}
                    placeholder="Selecciona nacionalidad..."
                    testId="new-player-nationality"
                    addLabel="Agregar nacionalidad"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="shoe_size">Talle de Botines (US)</Label>
                  <SelectWithAdd
                    catalogType="shoe_sizes"
                    value={newPlayer.shoe_size}
                    onChange={(value) => setNewPlayer({...newPlayer, shoe_size: value})}
                    placeholder="Selecciona talle..."
                    testId="new-player-shoe-size"
                    addLabel="Agregar talle"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="community_passport">Pasaporte Comunitario</Label>
                  <Select
                    value={newPlayer.community_passport}
                    onValueChange={(value) => setNewPlayer({...newPlayer, community_passport: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona" />
                    </SelectTrigger>
                    <SelectContent>
                      {COMMUNITY_PASSPORT_OPTIONS.map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="passport">Pasaporte</Label>
                  <Input
                    id="passport"
                    value={newPlayer.passport}
                    onChange={(e) => setNewPlayer({...newPlayer, passport: e.target.value})}
                    placeholder="Número de pasaporte"
                  />
                </div>
              </div>

              <div className="pt-2">
                <p className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  * Obligatorio: cargá al menos un contacto familiar (Madre, Padre o Tutor) con nombre y teléfono.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="mother_name">Nombre de la Madre</Label>
                  <Input
                    id="mother_name"
                    value={newPlayer.mother_name}
                    onChange={(e) => setNewPlayer({...newPlayer, mother_name: e.target.value})}
                    placeholder="Nombre completo"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mother_phone">Celular de la Madre</Label>
                  <Input
                    id="mother_phone"
                    value={newPlayer.mother_phone}
                    onChange={(e) => setNewPlayer({...newPlayer, mother_phone: e.target.value})}
                    placeholder="Número"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="father_name">Nombre del Padre</Label>
                  <Input
                    id="father_name"
                    value={newPlayer.father_name}
                    onChange={(e) => setNewPlayer({...newPlayer, father_name: e.target.value})}
                    placeholder="Nombre completo"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="father_phone">Celular del Padre</Label>
                  <Input
                    id="father_phone"
                    value={newPlayer.father_phone}
                    onChange={(e) => setNewPlayer({...newPlayer, father_phone: e.target.value})}
                    placeholder="Número"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="tutor_name">Nombre del Tutor</Label>
                  <Input
                    id="tutor_name"
                    value={newPlayer.tutor_name}
                    onChange={(e) => setNewPlayer({...newPlayer, tutor_name: e.target.value})}
                    placeholder="Nombre completo"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tutor_phone">Celular del Tutor</Label>
                  <Input
                    id="tutor_phone"
                    value={newPlayer.tutor_phone}
                    onChange={(e) => setNewPlayer({...newPlayer, tutor_phone: e.target.value})}
                    placeholder="Número"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="social_media">Redes Sociales</Label>
                <Input
                  id="social_media"
                  value={newPlayer.social_media}
                  onChange={(e) => setNewPlayer({...newPlayer, social_media: e.target.value})}
                  placeholder="@instagram, @tiktok, etc."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="origin_club">Equipo actual</Label>
                <SelectWithAdd
                  catalogType="teams"
                  value={newPlayer.origin_club}
                  onChange={(value) => setNewPlayer({...newPlayer, origin_club: value})}
                  placeholder="Selecciona equipo..."
                  testId="new-player-team"
                  addLabel="Agregar equipo"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="club_trajectory">Trayectoria de Clubes anteriores</Label>
                <Input
                  id="club_trajectory"
                  value={newPlayer.club_trajectory}
                  onChange={(e) => setNewPlayer({...newPlayer, club_trajectory: e.target.value})}
                  placeholder="Ej: Nacional (2020-2022), Fénix (2022-actual)"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="status">Estado</Label>
                  <Select
                    value={newPlayer.status}
                    onValueChange={(value) => setNewPlayer({...newPlayer, status: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Activo</SelectItem>
                      <SelectItem value="inactive">Inactivo</SelectItem>
                      <SelectItem value="trial">Prueba</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {isAdmin && (
                  <div className="space-y-2">
                    <Label htmlFor="scouted_by">Scout / Captador *</Label>
                    <Select
                      value={newPlayer.scouted_by || '__none__'}
                      onValueChange={(value) => setNewPlayer({...newPlayer, scouted_by: value === '__none__' ? '' : value})}
                    >
                      <SelectTrigger data-testid="new-player-scouted-by">
                        <SelectValue placeholder="Sin asignar" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Sin asignar</SelectItem>
                        {scouts.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.full_name || s.username}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Foto del Jugador</Label>
                <ImageUpload
                  value={newPlayer.photo_url}
                  onChange={(url) => setNewPlayer({...newPlayer, photo_url: url})}
                />
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowCreateDialog(false);
                  resetForm();
                }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-brand-600 hover:bg-brand-700"
                data-testid="submit-create-player"
              >
                Crear Jugador
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Scout Edit Dialog - Basic info only */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar Jugador</DialogTitle>
            <DialogDescription>
              Modifica la información básica del jugador. Una vez validado por el admin, no podrás editarlo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-first-name">Nombre *</Label>
                <Input
                  id="edit-first-name"
                  value={editingPlayer.first_name || ''}
                  onChange={(e) => setEditingPlayer({...editingPlayer, first_name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-last-name">Apellido *</Label>
                <Input
                  id="edit-last-name"
                  value={editingPlayer.last_name || ''}
                  onChange={(e) => setEditingPlayer({...editingPlayer, last_name: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-birth-date">Fecha de Nacimiento *</Label>
                <Input
                  id="edit-birth-date"
                  type="date"
                  value={editingPlayer.birth_date || ''}
                  onChange={(e) => setEditingPlayer({...editingPlayer, birth_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-document">Documento *</Label>
                <Input
                  id="edit-document"
                  value={editingPlayer.document || ''}
                  onChange={(e) => setEditingPlayer({...editingPlayer, document: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-phone">Teléfono</Label>
                <Input
                  id="edit-phone"
                  value={editingPlayer.phone || ''}
                  onChange={(e) => setEditingPlayer({...editingPlayer, phone: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editingPlayer.email || ''}
                  onChange={(e) => setEditingPlayer({...editingPlayer, email: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-address">Dirección</Label>
              <Input
                id="edit-address"
                value={editingPlayer.address || ''}
                onChange={(e) => setEditingPlayer({...editingPlayer, address: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-position">Posición</Label>
                <SelectWithAdd
                  catalogType="positions"
                  value={editingPlayer.position || ''}
                  onChange={(value) => setEditingPlayer({...editingPlayer, position: value})}
                  placeholder="Selecciona posición..."
                  testId="edit-player-position"
                  addLabel="Agregar posición"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-origin-club">Equipo actual</Label>
                <SelectWithAdd
                  catalogType="teams"
                  value={editingPlayer.origin_club || ''}
                  onChange={(value) => setEditingPlayer({...editingPlayer, origin_club: value})}
                  placeholder="Selecciona equipo..."
                  testId="edit-player-team"
                  addLabel="Agregar equipo"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowEditDialog(false);
                setSelectedPlayer(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleEditPlayer}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Players;